/**
 * useHumlanImages Hook
 *
 * Custom hook for managing Humla images state and API interactions.
 * Handles fetching, refreshing, and cleanup of session images.
 *
 * Features:
 * - Automatic polling for new images (configurable interval)
 * - Loading and error states
 * - Blob URL management (automatic cleanup)
 * - Manual refresh capability
 *
 * @author AI Journal Team
 * @created 2025-11-08
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  listSessionImages,
  refreshSessionImages,
  fetchImageBlob,
  deleteSessionImages
} from '../apiClient';

/**
 * Hook for managing Humla session images
 *
 * @param {string|null} sessionId - Session ID to fetch images for
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoRefresh - Enable automatic polling (default: false)
 * @param {number} options.refreshInterval - Polling interval in ms (default: 10000)
 * @param {boolean} options.fetchOnMount - Fetch images on mount (default: true)
 * @returns {Object} - {images, loading, error, refresh, cleanup}
 */
export const useHumlanImages = (sessionId, options = {}) => {
  const {
    autoRefresh = false,
    refreshInterval = 10000, // 10 seconds
    fetchOnMount = true
  } = options;

  // State
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrls, setImageUrls] = useState({}); // Map: filename -> blob URL

  // Refs for cleanup
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  /**
   * Fetch images from backend (already downloaded)
   */
  const fetchImages = useCallback(async () => {
    if (!sessionId) {
      console.log('[useHumlanImages] No sessionId, skipping fetch');
      setImages([]);
      setLoading(false);
      return;
    }

    console.log('[useHumlanImages] Fetching images for session:', sessionId);

    try {
      setLoading(true);
      setError(null);

      const response = await listSessionImages(sessionId);
      console.log('[useHumlanImages] Fetch response:', response);

      if (mountedRef.current) {
        setImages(response.images || []);
      }
    } catch (err) {
      console.error('[useHumlanImages] Error fetching images:', err);
      if (mountedRef.current) {
        setError(err.response?.data?.error || 'Failed to load images');
        setImages([]);
      }
    } finally {
      setLoading(false);
      console.log('[useHumlanImages] Fetch complete, loading set to false');
    }
  }, [sessionId]);

  /**
   * Refresh images from Humla API (download new ones)
   */
  const refresh = useCallback(async () => {
    if (!sessionId) {
      console.log('[useHumlanImages] No sessionId, skipping refresh');
      setLoading(false);
      return { success: false, images: [], count: 0 };
    }

    console.log('[useHumlanImages] Refreshing images from Humla API for session:', sessionId);

    try {
      setLoading(true);
      setError(null);

      const response = await refreshSessionImages(sessionId);
      console.log('[useHumlanImages] Refresh response:', response);

      if (mountedRef.current) {
        setImages(response.images || []);
      }

      return response;
    } catch (err) {
      console.error('[useHumlanImages] Error refreshing images:', err);
      if (mountedRef.current) {
        setError(err.response?.data?.error || 'Failed to refresh images');
        setImages([]); // Clear images on error
      }
      // Don't throw error - let component handle via error state
      return { success: false, images: [], count: 0 };
    } finally {
      setLoading(false);
      console.log('[useHumlanImages] Refresh complete, loading set to false');
    }
  }, [sessionId]);

  /**
   * Delete all images for session
   */
  const cleanup = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await deleteSessionImages(sessionId);

      if (mountedRef.current) {
        setImages([]);
        // Revoke all blob URLs
        Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
        setImageUrls({});
      }

      return response;
    } catch (err) {
      console.error('Error cleaning up Humla images:', err);
      throw err;
    }
  }, [sessionId, imageUrls]);

  /**
   * Get blob URL for an image (lazy loading)
   */
  const getImageBlobUrl = useCallback(async (filename) => {
    // Return cached URL if exists
    if (imageUrls[filename]) {
      return imageUrls[filename];
    }

    try {
      const blobUrl = await fetchImageBlob(sessionId, filename);

      if (mountedRef.current) {
        setImageUrls(prev => ({
          ...prev,
          [filename]: blobUrl
        }));
      }

      return blobUrl;
    } catch (err) {
      console.error(`Error fetching image blob: ${filename}`, err);
      throw err;
    }
  }, [sessionId, imageUrls]);

  /**
   * Fetch images on mount
   */
  useEffect(() => {
    if (fetchOnMount && sessionId) {
      fetchImages();
    }
  }, [sessionId, fetchOnMount, fetchImages]);

  /**
   * Setup auto-refresh polling
   */
  useEffect(() => {
    if (!autoRefresh || !sessionId) {
      return;
    }

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start new interval
    intervalRef.current = setInterval(() => {
      refresh();
    }, refreshInterval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, refreshInterval, sessionId, refresh]);

  /**
   * Cleanup blob URLs on unmount
   */
  useEffect(() => {
    return () => {
      mountedRef.current = false;

      // Revoke all blob URLs to free memory
      Object.values(imageUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageUrls]);

  return {
    images,
    loading,
    error,
    refresh,
    cleanup,
    getImageBlobUrl,
    hasImages: images.length > 0,
    imageCount: images.length
  };
};

export default useHumlanImages;
